import { makeAutoObservable, runInAction } from 'mobx';
import {
  DependentsChoice,
  ProductLineInterestChange,
  ProductLineSubRate,
  Subscriber,
  UpdatedPlanInfo,
} from 'types/electionService';
import { isSubscriberValid } from 'global/utils/election/subscriberValidation';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import { bpHistory } from 'global/bpHistory';
import {
  buildChoicesForSubscriber,
  buildInterestedProductLinesForSubscriber,
} from 'global/utils/election/productLineInitialOptions';
import SubscriberDelegate, { SubscriberDelegateConstructorParams } from './subscriberDelegate';
import ElectionsModule from './electionsModule';

interface ConstructorParams extends SubscriberDelegateConstructorParams {
  electionsModule: ElectionsModule;
}

/** An adapter that facilitates the elections logic on the subscriber. */
export default class SubscriberElectionsAdapter {
  private _subscriberDelegate: SubscriberDelegate;
  private _electionsModule: ElectionsModule;

  public get subscriberDelegate(): SubscriberDelegate {
    return this._subscriberDelegate;
  }

  public get pLineRates(): ProductLineSubRate[] {
    return this._electionsModule.statistics.subRates?.[this._subscriberDelegate.subscriber.id] || [];
  }

  /**
   * Indicates whether the subscriber is valid based on required subscriber rules.
   * Depends on {@link ElectionsModule.electionFlow} to select rules for the active flow.
   */
  public get isSubscriberValid(): boolean {
    return (
      this._electionsModule.electionPresenter !== null &&
      isSubscriberValid(
        this._electionsModule.electionPresenter,
        this._subscriberDelegate.subscriber,
        this._electionsModule.electionFlow,
      )
    );
  }

  public redirectToPortfolioDetails(productLineId: ProductLineId, additionalQueryParams?: string): void {
    const currentProductLinePortfolioId =
      this._electionsModule.products.find((product) => product.productLineId === productLineId)?.portfolioId || '';

    if (!currentProductLinePortfolioId) {
      throw new Error('Current product line portfolio id is not found');
    }

    const link = `/product-compare?type=available-products&portfolioIds=${currentProductLinePortfolioId}&cartId=${
      this._electionsModule.domainData.cartId
    }&productLineId=${productLineId}&periodId=${this._electionsModule.enrollmentPeriodId}&eeClass=${
      this._subscriberDelegate.subscriber.employeeClass || ''
    }${additionalQueryParams ? `&${additionalQueryParams}` : ''}`;

    bpHistory.push(link);
  }

  public async syncSelectedPlan(selectedPlanInfo: UpdatedPlanInfo): Promise<void> {
    await this._subscriberDelegate.updateSelectedPlan(selectedPlanInfo);
    await runInAction(async () => {
      await this._electionsModule.getStatistics();
    });
  }

  public async syncDependents(dependentsChoice: DependentsChoice): Promise<void> {
    await this._subscriberDelegate.updateDependentsInChoice(dependentsChoice);
    await runInAction(async () => {
      await this._electionsModule.getStatistics();
    });
  }

  public async syncDependentsInInterestedProductLines(dependentsChoice: DependentsChoice): Promise<void> {
    await this._subscriberDelegate.updateDependentsInInterestedProductLines(dependentsChoice);
  }

  public async syncProductLineInterest(change: ProductLineInterestChange): Promise<void> {
    await this._subscriberDelegate.updateProductLineInterest(change);
  }

  public async syncClassAndResetProductSelection(className: string): Promise<void> {
    await this._subscriberDelegate.updateClassAndResetProductSelection(className);
    await runInAction(async () => {
      await this._electionsModule.getStatistics();
    });
  }

  /**
   * Re-applies in-memory election defaults (choices + interested product lines; no BE save).
   *
   * Defaults are built once in the constructor. Later subscriber updates (e.g. dependents added via
   * the API) refresh `custom.dependents` but do not rebuild election rows, so in-memory choices and
   * interested product lines can become stale until a full reload. Call this to re-run the same merge
   * as on load — BE-saved rows still win; only missing lines get fresh defaults.
   */
  public syncElectionDefaults(): void {
    this._subscriberDelegate.setSubscriber(
      this._buildSubscriberWithElectionDefaults(this._subscriberDelegate.subscriber),
    );
  }

  private _buildSubscriberWithElectionDefaults(subscriber: Subscriber): Subscriber {
    return {
      ...subscriber,
      choice: buildChoicesForSubscriber(subscriber, this._electionsModule.products, this._electionsModule.rateStats),
      interestedProductLines: buildInterestedProductLinesForSubscriber(
        subscriber,
        this._electionsModule.domainProductLineIds,
      ),
    };
  }

  constructor({ subscriber, periodId, electionsModule }: ConstructorParams) {
    this._electionsModule = electionsModule;
    this._subscriberDelegate = new SubscriberDelegate({
      subscriber: this._buildSubscriberWithElectionDefaults(subscriber),
      periodId,
    });
    makeAutoObservable(this, {}, { autoBind: true });
  }
}
